import React from 'react'
import Link, { withPrefix } from 'gatsby-link'
import Helmet from 'react-helmet'

//  Components
import ThemeList from '../components/ThemeList/ThemeList'
import PageHeader from '../components/PageHeader/PageHeader'
import ThemeNavFooter from '../components/ThemeNavFooter/ThemeNavFooter'
import Button from '../components/Button/Button'
import ShareExhibit from '../components/ShareExhibit/ShareExhibit'
import caseStudyImageUrl, { ReactComponent as CaseStudyImage } from '../assets/svg/case-study-hospital-mortality.svg'
import exhibitUrl, { ReactComponent as Chart } from '../assets/svg/icon-chart.svg'

import img from '../assets/img/deaths.png'
import obesity from '../assets/img/obesity.png'

const Theme3Page = ({ data }) => (
  <main>
    <Helmet
      title={`${data.allThemesJson.edges[2].node.title}`}
    />
    <article>
      <PageHeader
        data={data.allThemesJson.edges[2].node}
        img={img}
      />

      <div className="section">
        <div className="section__content section__summary">
          <p>The COVID-19 pandemic has triggered a public health and economic crisis at a time when
            American health outcomes were already moving in the wrong direction. Decades long gains
            in life expectancy reversed after 2014, and adults of all races and ethnicities have
            been dying at increased
            rates.<sup className="note__link"><a href="#19">19</a></sup> These
            trends are likely to continue in the wake of more than 185,000 COVID-19 deaths, and they
            may be
            exacerbated by the deep decline in the use of health care services unrelated to the
            disease and the greatest increase in unemployment since the Great Depression.</p>
        </div>
      </div>
      <div className="section">
        <h2 className="section__title">Deaths from suicide, alcohol, and drug overdose</h2>
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h3>Deaths from suicide, alcohol, and drug overdose have all climbed over the past few
              decades and have been an important
              contributor to recent mortality trends.<sup className="note__link"><a
                href="#20">20</a></sup>
              <Link to="/deaths/#exhibit-8"><span className="exhibit__link"><Chart/></span></Link>
              These types of death differ significantly in terms of regional impact. <Link
                to="/deaths/#exhibit-9"><span className="exhibit__link"><Chart/></span></Link></h3>
            <p>Opioids have been a critical driver, particularly since 2012, when the impact of
              synthetic opioids like fentanyl expanded. Since then, synthetic opioid overdoses have
              grown from 6 percent of all overdose deaths to nearly half. <Link
                to="/deaths/#exhibit-10"><span className="exhibit__link"><Chart/></span></Link> In
              2018, the U.S. reported its first
              significant decline in drug overdose deaths in decades; however, the rate of synthetic
              opioid overdose deaths increased another 10 percent. Ten states and the District of
              Columbia attributed more than 20 deaths per 100,000 people to this cause.
              Unfortunately, preliminary estimates from the Centers for Disease Control and
              Prevention also show that drug overdose deaths jumped again in 2019, despite recent
              federal support and state efforts (preliminary 2019 data not shown in Exhibit 8).<sup
                className="note__link"><a href="#21">21</a></sup></p>
            <p>Research indicates that higher local unemployment or economic shocks may be
              associated with increased overdose deaths from opioids or other drugs, as well as poor
              mental health.<sup
                className="note__link"><a href="#22">22</a></sup> Experts have warned about
              potential consequences from COVID-19 on so-called deaths of despair, and recent
              reports indicate increases in overdose deaths in 2020.<sup className="note__link"><a
                href="#23">23</a></sup> The 38 states that, along with the District of Columbia,
              have expanded their Medicaid programs are much better prepared to address a
              pandemic-related rise in substance use. Studies have found that the ACA’s Medicaid
              expansion is associated with improvement in access to mental health care,<sup
                className="note__link"><a href="#24">24</a></sup> greater access to
              medication-assisted treatment,<sup className="note__link"><a
                href="#25">25</a></sup> and fewer opioid-related overdose deaths and
              hospitalizations.<sup className="note__link"><a href="#26">26</a></sup></p>
          </div>
          <div
            className="section__content section__content__wide ">
            <div id="exhibit-8" className="section__exhibit__padded section__exhibit">
              <h2 className="exhibit__title__block">Suicide and alcohol deaths rose modestly in 2018. Though
                drug
                overdoses dropped for the first time in decades, preliminary 2019 data shows that
                they
                have jumped back up</h2>
              <figure>
                <img src={withPrefix('/img/exhibits/exhibit-8-web.png')} alt=""/>
                <figcaption>
                  <p>Note: Preliminary 2019 drug overdose data from the CDC is not included in this
                    exhibit (see text).</p>
                  <p>Data: 2005–2018 National Vital Statistics System (NVSS), via CDC WONDER.</p>
                </figcaption>
              </figure>
              <ShareExhibit
                exhibit="exhibit-08"
                site={data.site.siteMetadata}
                social={data.allSocialJson.edges}
              />
            </div>
            <hr className="section__divider"/>
            <div id="exhibit-9" className="section__exhibit__padded section__exhibit">
              <h2 className="exhibit__title__block">Deaths from suicide, alcohol, and drug overdoses
                display significant regional variation</h2>
              <figure>
                <img className="exhibit__image__padded"
                     src={withPrefix('/img/exhibits/exhibit-9-web.png')} alt=""/>
                <figcaption>
                  <p>Note: District of Columbia not included in legend counts.</p>
                  <p>Data: 2018 National Vital Statistics System (NVSS), via CDC WONDER.</p>
                </figcaption>
              </figure>
              <ShareExhibit
                exhibit="exhibit-09"
                site={data.site.siteMetadata}
                social={data.allSocialJson.edges}
              />
            </div>
          </div>
        </div>
        <div id="exhibit-10" className="section__content section__exhibit section__exhibit__padded section__full">
          <h2 className="title__block">Opioids contributed heavily to the overall rise in overdose deaths between 2009 and 2018, largely due to synthetic opioids</h2>
          <figure>
            <img className="exhibit__image__padded" src={withPrefix('/img/exhibits/exhibit-10-web.png')} alt=""/>
            <figcaption>
              <p>Note: Data categories are overlaid, not stacked/aggregated; synthetic opioid
                deaths are from synthetic opioids other than methadone.</p>
              <p>Data: 2009–2018 National Vital Statistics System (NVSS), via CDC WONDER</p>
            </figcaption>
          </figure>
          <ShareExhibit
            exhibit="exhibit-10"
            site={data.site.siteMetadata}
            social={data.allSocialJson.edges}
          />
        </div>
      </div>
      <hr className="section__divider"/>
      <div className="section">
        <h2 className="section__title">Premature deaths from treatable conditions</h2>
        <div className="section__row section__row__2_up">
          <div className="section__content">
            <p>Americans can expect to live a shorter life today than they did in 2014.<sup
              className="note__link"><a href="#27">27</a></sup> A number of factors contribute to
              this decline, including deaths from suicide, alcohol, and drug overdose, but another
              is premature deaths from health care–treatable conditions — also known as mortality
              amenable to health care. The Scorecard tracks deaths before age 75 from acute and
              chronic causes that are considered treatable when they are identified early and well
              managed; examples include appendicitis, certain cancers, heart disease, and diabetes,
              among others. Higher mortality rates in these categories point to deficiencies in the
              health system.</p>
          </div>
          <div className="section__content">
            <p>Between 2012–2013 and 2016–2017, these mortality rates either increased slightly or
              were unchanged in 37 states.<sup className="note__link"><a href="#28">28</a></sup> Oklahoma, Arkansas, New Mexico, Kentucky, and Mississippi saw the biggest rise in premature deaths. States with the highest premature death rates also have lower life expectancies, <Link to="/deaths/#exhibit-11"><span className="exhibit__link"><Chart/></span></Link> and recent research linked life expectancy disparities to specific state policy choices.<sup className="note__link"><a href="#29">29</a></sup></p>
            <p>Black Americans, who have suffered disproportionately from the pandemic, are much
              more likely to die from some of these conditions, several of which are key risk
              factors for COVID-19.<sup className="note__link"><a
                href="#30">30</a></sup> Significant racial disparities in mortality amenable to
              health care exist in nearly every state. <Link to="/deaths/#exhibit-12"><span
                className="exhibit__link"><Chart/></span></Link> The pandemic threatens to
              exacerbate these trends even further through
              the disruption of primary care and other critical health services. Early data suggest
              elevated mortality from non-COVID-19 causes.<sup className="note__link"><a
                href="#31">31</a></sup></p>
          </div>
        </div>
        <div id="exhibit-11"
             className="section__content section__exhibit section__exhibit__padded section__full">
          <h2 className="title__block">Premature deaths from treatable conditions are closely linked
            to the wide variation in state life expectancy</h2>
          <figure>
            <img className="exhibit__image__padded"
                 src={withPrefix('/img/exhibits/exhibit-11-web.png')} alt=""/>
            <figcaption>
              <p>Data: Life Expectancy: United States Mortality Database, University of California,
                Berkeley; available at usa.mortality.org (data shown on 2020-05-11 12:22:03).
                Mortality Amenable to Health Care: Centers for Disease Control and Prevention, 2016
                and 2017 National Vital Statistics System (NVSS), All-County Micro Data, Restricted
                Use Files</p>
            </figcaption>
          </figure>
          <ShareExhibit
            exhibit="exhibit-11"
            site={data.site.siteMetadata}
            social={data.allSocialJson.edges}
          />
        </div>
        <hr className="section__divider"/>
        <div id="exhibit-12"
             className="section__content section__exhibit section__exhibit__padded section__full">
          <h2 className="title__block"> In every state, Black people are more likely to
            die early from treatable conditions, 2016–17</h2>
          <figure>
            <img className="exhibit__image__padded"
                 src={withPrefix('/img/exhibits/exhibit-12-web.png')} alt=""/>
            <figcaption>
              <p>Notes: Data for Black individuals not available for Idaho, Montana, Vermont, or
                Wyoming. States arranged in rank order based on Black mortality rates.</p>
              <p>Data: CDC 2016 and 2017 National Vital Statistics System (NVSS), All-County Micro
                Data, Restricted Use Files</p>
            </figcaption>
          </figure>
          <ShareExhibit
            exhibit="exhibit-12"
            site={data.site.siteMetadata}
            social={data.allSocialJson.edges}
          />
        </div>
      </div>
      <hr className="section__divider"/>
      <div className="section">
        <h2 className="section__title">Public health investment.</h2>
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h3>States are navigating
              unprecedented public health threats from COVID-19, as
              well as economic disruptions that have shrunk the tax
              revenues used to fund critical health and social services. But
              historically, most states have made only modest investments
              in their public health systems.</h3>
          </div>
          <div className="section__content">
            <p>Between 2014–2015 and
              2017–2018, per capita public health spending was flat in
              most states, and increases were modest in the states where
              funding did rise. <Link to="/deaths/#exhibit-13"><span
                className="exhibit__link"><Chart/></span></Link></p>
            <p>Not only is public health funding low relative to other health
              care spending, but public health dollars are stretched thin.
              Competing for funding are various initiatives for emergency
              preparedness, disease prevention, promoting healthy
              behaviors, and, increasingly, fighting the opioid epidemic.<sup
                className="note__link"><a href="#32">32</a></sup></p>
          </div>
        </div>
        <div id="exhibit-13"
             className="section__content section__exhibit section__exhibit__padded section__full">
          <h2 className="title__block">Most states have seen modest changes in per capita public
            health spending in recent years</h2>
          <figure>
            <img className="exhibit__image__padded"
                 src={withPrefix('/img/exhibits/exhibit-13-web.png')} alt=""/>
            <figcaption>
              <p>Notes: Estimates here include only state-based funding; federal public health funds
                are not included.</p>
              <p>Data: 2017–2018 estimates: Trust for America’s Health, The Impact of Chronic
                Underfunding on America’s Public Health System: Trends, Risks, and Recommendations,
                2019 (TFAH, Apr. 2019); 2014–2015 estimates: Trust for America’s Health, Investing
                in America’s Health: A State-by-State Look at Public Health Funding and Key Health
                Facts (TFAH, Apr. 2016).</p>
            </figcaption>
          </figure>
          <ShareExhibit
            exhibit="exhibit-13"
            site={data.site.siteMetadata}
            social={data.allSocialJson.edges}
          />
        </div>
      </div>
      <div className="section section__heavy">
        <div className="section__row section__row__2_up">
          <div className="section__content section__content__narrow">
            <h2>What Can Be Done?</h2>
          </div>
          <div className="section__content section__content__wide">
            <h3>Robust primary care systems are critical for addressing deaths from treatable chronic conditions that contribute to current mortality trends. States can pursue policies to increase primary care utilization, which is low across the U.S. They can also work to ensure access to mental health care, addiction treatment and improve access to life-saving overdose-reversal medications like naloxone, and pass legislation that sets guidelines or limits for opioid prescriptions. </h3>
          </div>
        </div>
      </div>
    </article>
    <ThemeNavFooter themes={data.allThemesJson.edges}/>
    <div className="section section__notes">
      <div className="section__row">
        <div className="section__content">
          <ol start="19" style={{ counterReset: 'item 18' }} className="list__notes">
            <li id="19">Steven H. Woolf and Heidi Schoomaker “<a
              href="https://jamanetwork.com/journals/jama/article-abstract/2756187"><span>Life Expectancy and Mortality Rates in the United States,1959–2017</span></a>,” <em>JAMA</em> 322,
              no. 20 (Nov. 26, 2019): 1996–2016.
            </li>
            <li id="20">Anne Case and Angus Deaton, <a
              href="https://press.princeton.edu/books/hardcover/9780691190785/deaths-of-despair-and-the-future-of-capitalism"><span>Deaths of Despair and the Future of Capitalism</span></a> (Princeton University Press, 2020).
            </li>
            <li id="21">Farida B. Ahmad, Lauren M. Rossen, and Paul Sutton, <a
              href="https://www.cdc.gov/nchs/nvss/vsrr/drug-overdose-data.htm"><span><em>Provisional Drug Overdose Death Counts</em></span></a> (National
              Center for Health Statistics, 2020).
            </li>
            <li id="22">Christopher J. Ruhm, “<a href="https://www.ncbi.nlm.nih.gov/pubmed/25839783"><span>Recessions, Healthy No More?</span></a>,” <em>Journal of Health Economics</em> 42 (July 2015): 17–28; Erin C. Strumpf et al., “<a href="https://www.sciencedirect.com/science/article/abs/pii/S0277953617304495"><span>Did the Great Recession Affect Mortality Rates in the Metropolitan United States? Effects on Mortality by Age, Gender and Cause of Death</span></a>,” <em>Social Science and Medicine</em> 189 (Sept. 2017): 11–16; Atheendar S. Venkataramani et al., “<a href="https://jamanetwork.com/journals/jamainternalmedicine/fullarticle/2757788"><span>Association Between Automotive Assembly Plant Closures and Opioid Overdose Mortality in the United States: A Difference in-Differences Analysis</span></a>,” <em>JAMA Internal Medicine</em> 180, no. 2 (Dec. 2019): 254–62; Kathleen A. Cagney et al., “<a href="https://ajph.aphapublications.org/doi/10.2105/AJPH.2013.301566"><span>The Onset of Depression During the Great Recession: Foreclosure and Older Adult Mental Health</span></a>,” <em>American Journal of Public Health</em> 104, no. 3 (Mar. 2014): 498–505; Alex Hollingsworth, Christopher J. Ruhm, and Kosali Simon, “<a href="https://www.nber.org/papers/w23192.pdf"><span>Macroeconomic Conditions and Opioid Abuse</span></a>,” Working Paper 23192 (National Bureau of Economic Research, March 2017).
            </li>
            <li id="23">Douglas Belkin, “<a href="https://www.wsj.com/articles/nations-top-mental-health-official-warns-against-a-second-coronavirus-lockdown-11590066006"><span>Nation’s Top Mental-Health Official Warns Against a Second Coronavirus Lockdown</span></a>,” <em>Wall Street Journal,</em> May 21, 2020; Stephen Petterson, John M. Westfall, and Benjamin F. Miller, <a href="https://wellbeingtrust.org/wp-content/uploads/2020/05/WBT_Deaths-of-Despair_COVID-19-FINAL-FINAL.pdf"><span><em>Projected Deaths of Despair During the Coronavirus Recession</em></span></a> (WellBeing Trust, May 2020); Brianna Ehley, “<a href="https://www.politico.com/news/2020/06/29/pandemic-unleashes-a-spike-in-overdose-deaths-345183"><span>Pandemic Unleashes a Spike in Overdose Deaths</span></a>,” <em>Politico,</em> updated July 2, 2020; and Jesse C. Baumgartner, Gabriella N. Aboulafia, and Sara R. Collins, “<a href="https://www.commonwealthfund.org/blog/2020/implications-covid-19-opioid-related-mortality"><span>The Implications of COVID-19 for Opioid-Related Mortality,</span></a>,” <em>To the Point</em> (blog), Commonwealth Fund, Aug. 28, 2020.
            </li>
            <li id="24">Jesse C. Baumgartner, Gabriella N. Aboulafia, and
              Audrey McIntosh, “<a
                href="https://www.commonwealthfund.org/blog/2020/aca-10-how-has-it-impacted-mental-health-care"><span>The ACA at 10: How Has It Impacted Mental Health Care?</span></a>,” <em>To
                the Point</em> (blog), Commonwealth Fund, Apr. 3, 2020.
            </li>
            <li id="25">Hefei Wen et al., “<a
              href="https://pubmed.ncbi.nlm.nih.gov/28296674/"><span>Impact of Medicaid Expansion on Medicaid-Covered Utilization of Buprenorphine for Opioid Use Disorder Treatment</span></a>,” <em>Medical
              Care</em> 55, no. 4 (Apr. 2017): 336–41.
            </li>
            <li id="26">Nicole Kravitz-Wirtz et al., “<a
              href="https://jamanetwork.com/journals/jamanetworkopen/fullarticle/2758476"><span>Association of Medicaid Expansion with Opioid Overdose Mortality in the United States</span></a>,” <em>JAMA
              Network Open</em> 3, no. 1 (Jan. 2020): e1919066; and Hefei Wen et al., “<a
              href="https://pubmed.ncbi.nlm.nih.gov/32202609/"><span>“Association Between Medicaid Expansion and Rates of Opioid Related Hospital Use</span></a>,” <em>JAMA
              Internal Medicine</em> 180, no.
              5 (Mar. 23, 2020): 753–59.
            </li>
            <li id="27">Kenneth D. Kochanek, Robert N. Anderson, and Elizabeth Arias, “<a href="https://www.cdc.gov/nchs/data/hestat/life-expectancy/life-expectancy-2018.htm"><span>Changes in Life Expectancy at Birth, 2010–2018</span></a>,” <em>NCHS E-Stat</em>, National Center for Health Statistics, updated Jan. 30, 2020.
            </li>
            <li id="28">Only Oklahoma had a change that exceeded + or - 0.5 standard deviations to meet
              our definition of “meaningful change” during this period.
            </li>
            <li id="29">Jennifer Karas Montez et al., “<a href="https://onlinelibrary.wiley.com/doi/full/10.1111/1468-0009.12469"><span>U.S. State Policies, Politics, and Life Expectancy</span></a>,” <em>Milbank Quarterly,</em> published online Aug. 4, 2020.
            </li>
            <li id="30">David C. Radley, “<a href="https://www.commonwealthfund.org/blog/2019/americans-die-prematurely-treatable-causes"><span>Diving into the Data: How Many Americans Die Prematurely from Treatable Causes?</span></a>,” <em>To the Point</em> (blog), Commonwealth Fund, Aug. 6, 2019.
            </li>
            <li id="31">Reis Thebault et al., “<a href="https://www.washingtonpost.com/graphics/2020/investigations/coronavirus-excess-deaths-heart/"><span>Heart Conditions Drove Spike in Deaths Beyond Those Attributed to COVID-19, Analysis Shows</span></a>,” <em>Washington
              Post,</em> July 2, 2020.
            </li>
            <li id="32">Trust for America’s Health, <a href="https://www.tfah.org/report-details/publichealthfunding2020/"><span><em>The Impact of Chronic Underfunding on America’s Public Health System: Trends, Risks, and Recommendations</em></span></a> (TFAH, Apr. 2020).
            </li>
          </ol>
        </div>
      </div>
    </div>
  </main>
)

export default Theme3Page

export const query = graphql`
    query Theme3Query {
        site {
            pathPrefix
            siteMetadata {
                title
                year
                subtitle
                siteUrl
                siteDescription
            }
        }
        allThemesJson {
            edges {
                node {
                    theme_id
                    title
                    path
                    stat {
                        suffix
                        number
                        text
                    }
                }
            }
        }
        allSocialJson {
            edges {
                node {
                    page_id
                    path
                    title
                    share_path
                    exhibit
                }
            }
        }
    }
`



// WEBPACK FOOTER //
// ./src/pages/deaths.js